import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Apple, Check, X, Loader2, RefreshCw, Radio } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { API } from "@/modules/collector_v2/lib/api";
import { MacAgentCaptureWizard } from "@/modules/collector_v2/components/collector/MacAgentCaptureWizard";

const ADMIN_TOKEN_STORAGE_KEY = "ppk_admin_token";
const POLL_INTERVAL_MS = 15000;

const getJwt = () =>
  localStorage.getItem("token") || localStorage.getItem("access_token") || "";

// Bridge JWT → admin token. Si el user es admin, recupera el token del
// backend principal automáticamente. Si no, devuelve null.
async function autoFetchAdminToken() {
  const jwt = getJwt();
  if (!jwt) return null;
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL || ""}/api/pppoker/v2/admin-token-bridge`,
      { headers: { Authorization: `Bearer ${jwt}` }, timeout: 6000 }
    );
    const tok = data?.admin_token;
    if (tok) {
      try {
        localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, tok);
      } catch {
        /* no bloqueante */
      }
      return tok;
    }
  } catch {
    /* silencioso */
  }
  return null;
}

/**
 * Card de Mac Agent: muestra capturas de UID nuevos pendientes de aprobación.
 * Polls cada 15 s. Permite aprobar (asignando club_id + max_tables a un slot
 * scoutN libre) o descartar.
 */
export function MacAgentCard({ onSessionCaptured }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyUid, setBusyUid] = useState(null);
  const [forms, setForms] = useState({});
  const [wizardOpen, setWizardOpen] = useState(false);

  const getToken = () => localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || "";

  const fetchPending = useCallback(async () => {
    let token = getToken();
    // ⭐ Si no hay admin token, intentar obtenerlo automáticamente vía bridge JWT
    if (!token) {
      token = await autoFetchAdminToken();
    }
    if (!token) {
      // sin JWT admin no podemos hacer nada — pero no mostramos error feo
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/v2/collector/accounts/pending-new`, {
        headers: { "X-Admin-Token": token },
      });
      setPending(r.data?.pending || []);
      setError(null);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || String(e);
      // si el token guardado es inválido (403), borrarlo y reintentar
      if (e.response?.status === 403) {
        localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
    const id = setInterval(fetchPending, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchPending]);

  const onApprove = async (uid) => {
    const form = forms[uid] || {};
    const clubId = parseInt(form.club_id, 10);
    const maxTables = parseInt(form.max_tables, 10) || 1;
    if (!clubId || Number.isNaN(clubId)) {
      toast.error("Ingresá club_id (numérico)");
      return;
    }
    setBusyUid(uid);
    try {
      const r = await axios.post(
        `${API}/v2/collector/accounts/pending-new/${uid}/approve`,
        null,
        {
          params: { club_id: clubId, max_tables: maxTables },
          headers: { "X-Admin-Token": getToken() },
        }
      );
      toast.success(
        `UID ${uid} aprobado como ${r.data.approved_as} (club ${r.data.club_id})`
      );
      toast.info(
        r.data.note ||
          "Reiniciá el collector con supervisorctl para cargar el nuevo scout."
      );
      fetchPending();
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyUid(null);
    }
  };

  const onReject = async (uid) => {
    if (!window.confirm(`¿Descartar UID ${uid}?`)) return;
    setBusyUid(uid);
    try {
      await axios.post(
        `${API}/v2/collector/accounts/pending-new/${uid}/reject`,
        null,
        { headers: { "X-Admin-Token": getToken() } }
      );
      toast.success(`UID ${uid} descartado`);
      fetchPending();
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyUid(null);
    }
  };

  const updateForm = (uid, field, value) =>
    setForms((prev) => ({
      ...prev,
      [uid]: { ...(prev[uid] || {}), [field]: value },
    }));

  const onCaptureSuccess = useCallback(() => { fetchPending(); onSessionCaptured?.(); }, [fetchPending, onSessionCaptured]);

  return (
    <Card data-testid="mac-agent-card" className="h-fit">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Apple className="h-4 w-4 text-slate-400" />
            Cuenta de Collector · conexión desde Mac
            {pending.length > 0 && (
              <Badge
                variant="outline"
                className="text-amber-300 border-amber-700 ml-1"
                data-testid="mac-agent-pending-count"
              >
                {pending.length}
              </Badge>
            )}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="default"
              onClick={() => setWizardOpen(true)}
              data-testid="mac-agent-capture-btn"
              className="h-7 px-2 bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Radio className="h-3.5 w-3.5 mr-1" />
              Conectar cuenta desde Mac
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={fetchPending}
              disabled={loading}
              data-testid="mac-agent-refresh-btn"
              className="h-7 px-2"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-500" data-testid="collector-mac-steps">
          <li>Pulsá Conectar cuenta desde Mac y esperá el aviso de captura activa.</li>
          <li>Iniciá sesión en PPPoker de la Mac con la cuenta observadora que usaremos para mesas.</li>
          <li>Revisá el UID capturado. Si es una cuenta nueva, asignale su club y una mesa para la primera prueba.</li>
          <li>Después iniciá Collector con el control de abajo y verificá la cuenta en Conexión PPPoker.</li>
        </ol>
        <p className="text-xs text-amber-600" data-testid="collector-session-handoff-note">
          Capturar no inicia Collector ni confirma una conexión. Al iniciar, Collector usa su propia conexión y puede cerrar la sesión de esa cuenta en la Mac. Usá una cuenta observadora distinta de la de cargas.
        </p>
        <p className="text-xs text-slate-500">Primera validación: revisar mesas admitidas y descartadas, excluyendo MTT, SpinUp y SNG. Los datos de manos siguen pendientes de validación.</p>

        {error && (
          <div className="text-xs text-red-400 bg-red-950/30 border border-red-900 rounded px-2 py-1">
            {error}
          </div>
        )}
        {!loading && pending.length === 0 && !error && (
          <div className="text-xs text-slate-500 italic">
            No hay UIDs nuevos pendientes. Cuando el agente capture un login con
            UID no configurado, va a aparecer acá.
          </div>
        )}
        {pending.map((item) => {
          const form = forms[item.uid] || {};
          const busy = busyUid === item.uid;
          return (
            <div
              key={item.uid}
              data-testid={`mac-agent-pending-${item.uid}`}
              className="rounded border border-slate-700 bg-slate-900/50 p-2 space-y-2"
            >
              <div className="flex items-center justify-between gap-2 text-xs">
                <div className="space-y-0.5 flex-1 min-w-0">
                  <div className="font-mono text-amber-300 text-[13px]">
                    UID {item.uid}
                  </div>

                  <div className="text-slate-500 text-[11px]">
                    app: {item.app_version || "?"} · capturas:{" "}
                    {item.capture_count} · último:{" "}
                    {item.last_seen?.substr(11, 8) || "—"}
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className="text-amber-400 border-amber-700 flex-shrink-0"
                >
                  pending
                </Badge>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  type="number"
                  placeholder="club_id"
                  value={form.club_id || ""}
                  onChange={(e) =>
                    updateForm(item.uid, "club_id", e.target.value)
                  }
                  className="h-7 text-xs w-28"
                  data-testid={`mac-agent-club-input-${item.uid}`}
                />
                <Input
                  type="number"
                  placeholder="max_tables"
                  value={form.max_tables || ""}
                  onChange={(e) =>
                    updateForm(item.uid, "max_tables", e.target.value)
                  }
                  className="h-7 text-xs w-24"
                  data-testid={`mac-agent-maxtables-input-${item.uid}`}
                />
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => onApprove(item.uid)}
                  disabled={busy}
                  className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`mac-agent-approve-${item.uid}`}
                >
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <>
                      <Check className="h-3.5 w-3.5 mr-1" />
                      Aprobar
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onReject(item.uid)}
                  disabled={busy}
                  className="h-7 px-2 text-red-400 hover:text-red-300"
                  data-testid={`mac-agent-reject-${item.uid}`}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
      <MacAgentCaptureWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={onCaptureSuccess}
      />
    </Card>
  );
}
